// The Ask conversation, shared by the Ask page and the voice assistant so a
// question asked by voice on Today continues on the Ask screen.

import { useEffect, useState, useSyncExternalStore } from 'react';
import { api, apiUrl } from './api.ts';
import { devicePrefs } from './device.ts';
import { useDevice } from './hooks.ts';
import { setServerSpeech } from './voice.ts';

export interface Turn {
  role: 'user' | 'assistant';
  content: string;
  error?: boolean;
  /** Asked by voice. */
  voice?: boolean;
  /** Identifies the answer a stream writes into. */
  id?: string;
}

const STORE_KEY = 'arlo.ask';
let turns: Turn[] = load();
const listeners = new Set<() => void>();

function load(): Turn[] {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY) ?? '[]') as Turn[];
  } catch {
    return [];
  }
}

function set(next: Turn[]) {
  turns = next.slice(-30);
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(turns));
  } catch {
    /* ignore */
  }
  listeners.forEach((l) => l());
}

export function useConversation(): Turn[] {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => turns,
  );
}

export function clearConversation(): void {
  set([]);
}

let busy = false;
export const askBusy = () => busy;

/**
 * Ask a question; the answer streams into the conversation. `onText` gets
 * each text delta (the voice assistant speaks sentences as they complete).
 * Resolves with the full answer, or throws with a friendly message.
 */
export async function ask(question: string, opts: { voice?: boolean; onText?: (delta: string) => void; signal?: AbortSignal } = {}): Promise<string> {
  const q = question.trim();
  if (!q) return '';
  busy = true;
  const history = turns.filter((t) => !t.error).map(({ role, content }) => ({ role, content }));
  const id = Math.random().toString(36).slice(2);
  set([...turns, { role: 'user', content: q, voice: opts.voice }, { role: 'assistant', content: '', id }]);
  // Update this answer by id: another question may have been asked since.
  const update = (fn: (a: Turn) => Turn) => set(turns.map((t) => (t.id === id ? fn(t) : t)));
  let answer = '';
  try {
    const d = devicePrefs();
    const res = await fetch(apiUrl('/api/ask'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(d.key ? { Authorization: `Bearer ${d.key}` } : {}) },
      body: JSON.stringify({ question: q, history, mode: opts.voice ? 'voice' : undefined }),
      signal: opts.signal,
    });
    if (!res.ok || !res.body) {
      const b = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(b.error ?? `Error ${res.status}`);
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf('\n\n')) >= 0) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const ev = /^event: (.*)$/m.exec(chunk)?.[1];
        const data = /^data: (.*)$/m.exec(chunk)?.[1];
        if (!ev || data === undefined) continue;
        const payload = JSON.parse(data);
        if (ev === 'text') {
          answer += payload;
          update((a) => ({ ...a, content: a.content + payload }));
          opts.onText?.(payload);
        }
        if (ev === 'error') throw new Error(payload.message);
      }
    }
    return answer;
  } catch (err) {
    const message = (err as Error).name === 'AbortError' ? answer || 'Stopped.' : (err as Error).message || 'Something went wrong.';
    update((a) => ({ ...a, content: message, error: (err as Error).name !== 'AbortError' }));
    throw new Error(message);
  } finally {
    busy = false;
  }
}

export interface AskInfo {
  enabled: boolean;
  voice: 'neural' | 'device';
  voices: { id: string; label: string }[];
  /** The server can transcribe speech (for browsers that can't). */
  stt: boolean;
}

let info: AskInfo | undefined;

/** Is AI switched on, and are neural voices available? (null while checking) */
export function useAskInfo(): AskInfo | null {
  const d = useDevice();
  const [state, setState] = useState<AskInfo | null>(info ?? null);
  useEffect(() => {
    if (!d.key) return setState({ enabled: false, voice: 'device', voices: [], stt: false });
    api<AskInfo>('/api/ask')
      .then((r) => {
        info = { enabled: !!r.enabled, voice: r.voice === 'neural' ? 'neural' : 'device', voices: r.voices ?? [], stt: !!r.stt };
        setServerSpeech(info.stt);
        setState(info);
      })
      .catch(() => setState({ enabled: false, voice: 'device', voices: [], stt: false }));
  }, [d.key, d.serverUrl]);
  return state;
}
